import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Modal } from '../common';
import { SPADES_TIERS, formatPrice } from '../../constants';
import { authApi } from '../../services/authApi';
import './AddSpadesModal.css';

/**
 * Add Spades purchase modal with a simple review flow:
 * 1. Select amount
 * 2. Review purchase and continue to Stripe Checkout
 */
function AddSpadesModal({ isOpen, onClose, onPurchaseComplete, preselectedTier = null }) {
  const [step, setStep] = useState(preselectedTier ? 2 : 1);
  const [selectedTier, setSelectedTier] = useState(preselectedTier || SPADES_TIERS[0]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [purchaseError, setPurchaseError] = useState(null);

  const handleClose = useCallback(() => {
    setStep(preselectedTier ? 2 : 1);
    setSelectedTier(preselectedTier || SPADES_TIERS[0]);
    setIsProcessing(false);
    setPurchaseError(null);
    onClose();
  }, [onClose, preselectedTier]);

  const handleSelectTier = (tier) => {
    setSelectedTier(tier);
    setStep(2);
  };

  const handlePurchase = async () => {
    setIsProcessing(true);
    setPurchaseError(null);

    try {
      const result = await authApi.purchaseSpades(selectedTier.amount);
      onPurchaseComplete({
        ...result,
        spadeAmount: selectedTier.amount,
      });
      handleClose();
    } catch (error) {
      setPurchaseError(error.message || 'Failed to start Stripe checkout.');
    } finally {
      setIsProcessing(false);
    }
  };

  const today = new Date();
  const formattedDate = today.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Add Spades" className="add-spades-modal">
      <div className="add-spades-modal__content">
        {step === 1 ? (
          <div className="add-spades-modal__tiers">
            <p className="add-spades-modal__subtitle">Select an amount</p>
            {SPADES_TIERS.map((tier) => (
              <div key={tier.id} className="add-spades-modal__tier">
                <div className="add-spades-modal__tier-info">
                  <span className="add-spades-modal__spade-icon">♠</span>
                  <span>{tier.display}</span>
                </div>
                <button
                  type="button"
                  className="add-spades-modal__tier-btn"
                  onClick={() => handleSelectTier(tier)}
                >
                  {formatPrice(tier.price)}
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="add-spades-modal__review">
            <div className="add-spades-modal__summary">
              <div className="add-spades-modal__summary-row">
                <div className="add-spades-modal__summary-item">
                  <span className="add-spades-modal__spade-icon">♠</span>
                  <span>{selectedTier.display}</span>
                </div>
                <span className="add-spades-modal__summary-price">
                  {formatPrice(selectedTier.price)}
                </span>
              </div>
              <p className="add-spades-modal__summary-note">
                One-time charge on {formattedDate}
              </p>
            </div>

            <div className="add-spades-modal__totals">
              <div className="add-spades-modal__total-row">
                <span>Subtotal</span>
                <span>{formatPrice(selectedTier.price)}</span>
              </div>
              <div className="add-spades-modal__total-row">
                <span>Total</span>
                <span>{formatPrice(selectedTier.price)}</span>
              </div>
            </div>

            <div className="add-spades-modal__divider" />

            <div className="add-spades-modal__review-copy">
              <p className="add-spades-modal__section-title">Secure Checkout</p>
              <p className="add-spades-modal__checkout-note">
                You will be redirected to Stripe Checkout to enter your payment details securely.
              </p>
            </div>

            {purchaseError && (
              <p className="add-spades-modal__error add-spades-modal__error--purchase">
                {purchaseError}
              </p>
            )}

            <div className="add-spades-modal__actions">
              <button
                type="button"
                className="add-spades-modal__secondary-btn"
                onClick={() => setStep(1)}
                disabled={isProcessing}
              >
                Change Amount
              </button>
              <button
                type="button"
                className="add-spades-modal__purchase-btn"
                onClick={handlePurchase}
                disabled={isProcessing}
              >
                {isProcessing ? 'Redirecting...' : 'Continue to Stripe'}
              </button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}

AddSpadesModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onPurchaseComplete: PropTypes.func,
  preselectedTier: PropTypes.shape({
    id: PropTypes.string,
    amount: PropTypes.number,
    price: PropTypes.number,
    display: PropTypes.string,
  }),
};

AddSpadesModal.defaultProps = {
  onPurchaseComplete: () => {},
  preselectedTier: null,
};

export default AddSpadesModal;
